import React, { useState } from 'react';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../hooks/useAuth';
import { CreateTenantModal } from './CreateTenantModal';
import { SwitchWorkspaceReasonModal } from './SwitchWorkspaceReasonModal';
import type { Tenant } from '../context/TenantContext';

export const WorkspaceSwitcher: React.FC = () => {
  const { activeTenant, tenants, switchTenant, userRole } = useTenant();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingTenant, setPendingTenant] = useState<Tenant | null>(null);

  const isSuperAdmin = ['super_admin', 'admin'].includes(userRole.toLowerCase());
  const ownOrgId = user?.organization_id || null;

  // Same-org switches apply immediately; entering another org first collects a reason.
  const requestSwitch = (tenant: Tenant) => {
    if (tenant.tenant_id === activeTenant?.tenant_id) {
      return;
    }
    if (ownOrgId && tenant.tenant_id !== ownOrgId) {
      setPendingTenant(tenant);
      return;
    }
    switchTenant(tenant.tenant_id);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          backgroundColor: '#1f3a2b',
          color: '#ecfdf5',
          border: '1px solid #059669',
          borderRadius: '6px',
          padding: '8px 12px',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: 'pointer'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
          <span style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#10b981',
            flexShrink: 0
          }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {activeTenant ? activeTenant.name : 'Select Workspace'}
          </span>
        </div>
        <span style={{ fontSize: '0.75rem', opacity: 0.8, marginLeft: '4px' }}>▼</span>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '6px',
          width: '240px',
          maxHeight: '340px',
          overflowY: 'auto',
          backgroundColor: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.8)',
          zIndex: 999999
        }}>
          <div style={{ padding: '8px 12px', fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Workspaces
          </div>
          {tenants.map(tenant => (
            <button
              key={tenant.tenant_id}
              onClick={() => {
                setIsOpen(false);
                requestSwitch(tenant);
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '8px 12px',
                backgroundColor: tenant.tenant_id === activeTenant?.tenant_id ? '#374151' : 'transparent',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '0.875rem'
              }}
            >
              <span>{tenant.name}</span>
              {tenant.tenant_id === activeTenant?.tenant_id && (
                <span style={{ color: '#10b981', fontSize: '0.875rem' }}>✓</span>
              )}
            </button>
          ))}

          {isSuperAdmin && (
            <div style={{ borderTop: '1px solid #374151', marginTop: '4px', padding: '4px' }}>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsModalOpen(true);
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  backgroundColor: 'transparent',
                  color: '#3b82f6',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              >
                + Create New Tenant
              </button>
            </div>
          )}
        </div>
      )}

      <CreateTenantModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <SwitchWorkspaceReasonModal
        isOpen={pendingTenant !== null}
        workspaceName={pendingTenant?.name || ''}
        onCancel={() => setPendingTenant(null)}
        onConfirm={(reason) => {
          const target = pendingTenant;
          setPendingTenant(null);
          if (target) {
            switchTenant(target.tenant_id, reason);
          }
        }}
      />
    </div>
  );
};
